import { useBranch } from "../contexts/BranchContext";
import { useTheme } from "../contexts/ThemeContext";
import { useLang } from "../contexts/LanguageContext";
import { motion } from "framer-motion";
import { Separator } from "../components/ui/separator";
import { Flame, MapPin, Phone, Clock } from "lucide-react";
import { branchGalleryUrl } from "../utils/branchGallery";

const branchImages = {
  caddebostan: {
    story: branchGalleryUrl("caddebostan", 4),
    vision: branchGalleryUrl("caddebostan", 5),
    hero: branchGalleryUrl("caddebostan", 6),
  },
  zeytinburnu: {
    story: branchGalleryUrl("zeytinburnu", 4),
    vision: branchGalleryUrl("zeytinburnu", 5),
    hero: branchGalleryUrl("zeytinburnu", 6),
  },
};

const fadeUp = {
  hidden: { opacity: 0, y: 30 },
  visible: (i = 0) => ({ opacity: 1, y: 0, transition: { duration: 0.7, delay: i * 0.15, ease: [0.25, 0.46, 0.45, 0.94] } }),
};

const branchesStaticData = [
  { key: "zeytin", address: "Seyitnizam, Mevlana Cd. No:81-83/D, 34015 Zeytinburnu/İstanbul", phone: "0(553) 889 01 46" },
  { key: "cadde", address: "Caddebostan, Yener Sk. No:5 D:1a, 34728 Kadıköy/İstanbul", phone: "0(506) 382 46 46" },
];

export default function AboutPage() {
  const { selectedBranch } = useBranch();
  const { theme } = useTheme();
  const { t } = useLang();
  const dk = theme === "dark";
  const textH = dk ? "text-white" : "text-[#1A1A1A]";
  const textP = dk ? "text-[#999]" : "text-[#666]";
  const cardBg = dk ? "bg-[#121212]" : "bg-white";
  const borderS = dk ? "border-white/5" : "border-black/5";
  const imgs = branchImages[selectedBranch];

  return (
    <div data-testid="about-page" className="min-h-screen">
      <section className="relative h-[40vh] md:h-[50vh] overflow-hidden">
        <div className="absolute inset-0 bg-[#121212]">
          <img src={imgs.hero} alt={t.aboutPage.label} className="w-full h-full object-cover opacity-30" />
          <div className="absolute inset-0 bg-gradient-to-t from-[#0a0a0a] via-transparent to-transparent" />
        </div>
        <div className="relative z-10 h-full flex flex-col justify-end pb-12 md:pb-16 max-w-[1200px] mx-auto px-6">
          <motion.span initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="font-body text-xs uppercase tracking-[0.3em] text-[#8B1A1A] mb-3">{t.aboutPage.label}</motion.span>
          <motion.h1 initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6, delay: 0.1 }} className="font-heading text-4xl sm:text-5xl lg:text-6xl text-white">
            {t.aboutPage.title1}<span className="italic text-[#8B1A1A]">{t.aboutPage.title2}</span>
          </motion.h1>
        </div>
      </section>

      <section className="py-20 md:py-32 max-w-[1200px] mx-auto px-6" data-testid="story-section">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
          <motion.div initial="hidden" whileInView="visible" viewport={{ once: true }} variants={fadeUp}>
            <img src={imgs.story} alt="Tepsi Et" className="w-full aspect-[4/3] object-cover" />
          </motion.div>
          <motion.div initial="hidden" whileInView="visible" viewport={{ once: true }} variants={fadeUp} custom={1}>
            <Flame className="w-6 h-6 text-[#8B1A1A] mb-4" />
            <h2 className={`font-heading text-3xl sm:text-4xl ${textH} mb-6 leading-tight`}>{t.aboutPage.storyLabel} <span className="italic text-[#8B1A1A]">{t.aboutPage.storyTitle}</span></h2>
            <div className={`space-y-4 font-body text-base ${textP} leading-relaxed`}>
              <p>{t.aboutPage.story1}</p>
              <p>{t.aboutPage.story2}</p>
              <p>{t.aboutPage.story3}</p>
            </div>
          </motion.div>
        </div>
      </section>

      <Separator className="bg-[#8B1A1A]/10 max-w-[1200px] mx-auto" />

      <section className="py-20 md:py-32 max-w-[1200px] mx-auto px-6" data-testid="about-vision-section">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
          <motion.div initial="hidden" whileInView="visible" viewport={{ once: true }} variants={fadeUp} className="order-2 lg:order-1">
            <span className="font-body text-xs uppercase tracking-[0.3em] text-[#8B1A1A] block mb-4">{t.aboutPage.visionLabel}</span>
            <h2 className={`font-heading text-3xl sm:text-4xl ${textH} mb-6 leading-tight`}>{t.aboutPage.visionTitle} <span className="italic text-[#8B1A1A]">&</span></h2>
            <div className={`space-y-6 font-body text-base ${textP} leading-relaxed`}>
              <div>
                <h3 className="font-heading text-xl text-[#8B1A1A] mb-3">{t.aboutPage.visionSubtitle}</h3>
                <p>{t.aboutPage.visionDesc}</p>
              </div>
              <div>
                <h3 className="font-heading text-xl text-[#8B1A1A] mb-3">{t.aboutPage.missionSubtitle}</h3>
                <p>{t.aboutPage.missionDesc}</p>
              </div>
            </div>
            <div className="mt-8 flex gap-12">
              {[{n:"2",l:t.aboutPage.stats.branches},{n:"50+",l:t.aboutPage.stats.variety}].map((s) => (
                <div key={s.l}><span className="font-heading text-3xl text-[#8B1A1A]">{s.n}</span><p className={`font-body text-xs ${textP} uppercase tracking-wider mt-1`}>{s.l}</p></div>
              ))}
            </div>
          </motion.div>
          <motion.div initial="hidden" whileInView="visible" viewport={{ once: true }} variants={fadeUp} custom={1} className="order-1 lg:order-2">
            <img src={imgs.vision} alt={t.aboutPage.visionLabel} className="w-full aspect-[3/4] object-cover" />
          </motion.div>
        </div>
      </section>

      <Separator className="bg-[#8B1A1A]/10 max-w-[1200px] mx-auto" />

      <section className="py-20 md:py-32 max-w-[1200px] mx-auto px-6" data-testid="branches-section">
        <motion.div initial="hidden" whileInView="visible" viewport={{ once: true }} variants={fadeUp} className="text-center mb-16">
          <span className="font-body text-xs uppercase tracking-[0.3em] text-[#8B1A1A] block mb-3">{t.aboutPage.branchLabel}</span>
          <h2 className={`font-heading text-3xl sm:text-4xl lg:text-5xl ${textH}`}>{t.aboutPage.branchTitle1} <span className="italic text-[#8B1A1A]">{t.aboutPage.branchTitle2}</span></h2>
        </motion.div>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
          {branchesStaticData.map((branch, i) => {
            const bt = t.branches[branch.key];
            return (
              <motion.div key={branch.key} initial="hidden" whileInView="visible" viewport={{ once: true }} variants={fadeUp} custom={i} data-testid={`branch-card-${branch.key}`} className={`border ${borderS} hover:border-[#8B1A1A]/30 transition-colors duration-500 p-8 ${cardBg}`}>
                <h3 className="font-heading text-2xl text-[#8B1A1A] mb-4">{bt.name}</h3>
                <p className={`font-body text-sm ${textP} leading-relaxed mb-6`}>{bt.desc}</p>
                <div className="space-y-3">
                  <div className={`flex items-start gap-3 font-body text-sm ${dk ? "text-[#ccc]" : "text-[#444]"}`}><MapPin className="w-4 h-4 text-[#8B1A1A] mt-0.5 shrink-0" /><span>{branch.address}</span></div>
                  <div className={`flex items-center gap-3 font-body text-sm ${dk ? "text-[#ccc]" : "text-[#444]"}`}><Phone className="w-4 h-4 text-[#8B1A1A] shrink-0" /><span>{branch.phone}</span></div>
                  <div className={`flex items-center gap-3 font-body text-sm ${dk ? "text-[#ccc]" : "text-[#444]"}`}><Clock className="w-4 h-4 text-[#8B1A1A] shrink-0" /><span>{bt.hours}</span></div>
                </div>
                <p className="font-body text-xs text-[#8B1A1A] uppercase tracking-wider mt-4">{bt.note}</p>
              </motion.div>
            );
          })}
        </div>
      </section>
    </div>
  );
}
